import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { memo, useState } from "react";

import { resolveUploadUrl } from "../../../../infrastructure/utils/resolveUploadUrl";
import FilePreviewLightbox from "./FilePreviewLightbox";
import { isImageFile, isPdfFile } from "./filePreviewUtils";

const ImageThumbnail = memo(
  ({ label, thumbSrc, size, onClick, onBroken }) => {
    const [failed, setFailed] = useState(false);

    return (
      <Paper
        variant="outlined"
        onClick={failed ? undefined : onClick}
        sx={{
          width: size,
          cursor: failed ? "default" : "pointer",
          overflow: "hidden",
          borderRadius: 1.5,
          transition: "box-shadow 0.15s",
          "&:hover": failed
            ? {}
            : {
                boxShadow: 2,
              },
        }}
      >
        <Box
          sx={{
            width: size,
            height: size,
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!failed && thumbSrc ? (
            <Box
              component="img"
              src={thumbSrc}
              alt={label}
              loading="lazy"
              decoding="async"
              onError={() => {
                setFailed(true);
                onBroken?.();
              }}
              sx={{
                width: size,
                height: size,
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <BrokenImageOutlinedIcon color="disabled" />
          )}
        </Box>
        <Typography
          variant="caption"
          display="block"
          px={0.75}
          py={0.5}
          noWrap
          title={label}
          sx={{ maxWidth: size, lineHeight: 1.2 }}
        >
          {label}
        </Typography>
      </Paper>
    );
  }
);

ImageThumbnail.displayName = "ImageThumbnail";
ImageThumbnail.propTypes = {
  label: PropTypes.string.isRequired,
  thumbSrc: PropTypes.string,
  size: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  onBroken: PropTypes.func,
};

const FileAssetCard = memo(({ label, url, resolvedUrl, size }) => {
  const FileIcon = isPdfFile(url) ? PictureAsPdfOutlinedIcon : DescriptionOutlinedIcon;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: size,
        minHeight: size,
        p: 1,
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 0.5,
      }}
    >
      <FileIcon color="action" sx={{ fontSize: 32, mt: 0.5 }} />
      <Typography
        variant="caption"
        align="center"
        noWrap
        title={label}
        sx={{ width: "100%", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Button
        size="small"
        variant="text"
        endIcon={<OpenInNewIcon fontSize="inherit" />}
        href={resolvedUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ fontSize: "0.7rem", py: 0.25, minWidth: 0 }}
      >
        مشاهده
      </Button>
    </Paper>
  );
});

FileAssetCard.displayName = "FileAssetCard";
FileAssetCard.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
  resolvedUrl: PropTypes.string,
  size: PropTypes.number.isRequired,
};

const FileThumbnailGrid = ({ files = [], fileBaseUrl = "" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const thumbSize = isMobile ? 72 : 88;
  const [preview, setPreview] = useState(null);
  const [brokenImages, setBrokenImages] = useState(() => new Set());

  if (!files.length) return null;

  const markBroken = (label) => {
    setBrokenImages((prev) => {
      const next = new Set(prev);
      next.add(label);
      return next;
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, ${thumbSize}px)`,
          gap: 1.25,
          justifyContent: "start",
          maxHeight: 220,
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        {files.map((file) => {
          const resolvedUrl = resolveUploadUrl(file.url, fileBaseUrl);
          const treatAsImage =
            isImageFile(file.url) && !brokenImages.has(file.label);

          if (treatAsImage) {
            return (
              <ImageThumbnail
                key={file.label}
                label={file.label}
                thumbSrc={resolvedUrl}
                size={thumbSize}
                onClick={() =>
                  setPreview({ label: file.label, url: file.url })
                }
                onBroken={() => markBroken(file.label)}
              />
            );
          }

          return (
            <FileAssetCard
              key={file.label}
              label={file.label}
              url={file.url}
              resolvedUrl={resolvedUrl}
              size={thumbSize}
            />
          );
        })}
      </Box>

      <FilePreviewLightbox
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        label={preview?.label}
        url={preview?.url}
        fileBaseUrl={fileBaseUrl}
      />
    </>
  );
};

FileThumbnailGrid.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ),
  fileBaseUrl: PropTypes.string,
};

export default FileThumbnailGrid;
